import { useEffect } from 'react';

export interface UseDataLayerResetProps {
  triggerReset: boolean;
  preserveVerticalLinesOnReset: boolean;
  loadingSensors: Set<string> | null | undefined;
  initialFeatures: Record<string, Record<string, boolean>>;
  cancelAllRequests: () => void;
  clearLoadingSensor?: (sensorName: string) => void;
  clearAllLines: () => void;
  clearIntervalLines: () => void;
  resetAllFeatures: (initialFeatures: Record<string, Record<string, boolean>>) => void;
  forceResetToInitial: () => void;
  onResetDataLayer: () => void;
  forceReloadAllData: () => void;
}

/**
 * Manages data layer reset logic
 * Handles clearing loading sensors, vertical lines, resetting features, and reloading data
 */
export function useDataLayerReset({
  triggerReset,
  preserveVerticalLinesOnReset,
  loadingSensors,
  initialFeatures,
  cancelAllRequests,
  clearLoadingSensor,
  clearAllLines,
  clearIntervalLines,
  resetAllFeatures,
  forceResetToInitial,
  onResetDataLayer,
  forceReloadAllData,
}: UseDataLayerResetProps): void {
  useEffect(() => {
    if (!triggerReset) return;

    const handleResetTrigger = async () => {
      try {
        cancelAllRequests();

        // Clear all loading sensors from the global loading state
        if (loadingSensors && clearLoadingSensor && loadingSensors.size > 0) {
          const sensorsToClean = Array.from(loadingSensors);
          sensorsToClean.forEach((sensorName) => {
            clearLoadingSensor(sensorName);
          });
        }

        // Clear vertical lines if preserveVerticalLinesOnReset is false
        if (!preserveVerticalLinesOnReset) {
          clearAllLines();
          clearIntervalLines();
        }

        await new Promise((resolve) => setTimeout(resolve, 100));
        resetAllFeatures(initialFeatures);

        forceResetToInitial();

        await new Promise((resolve) => setTimeout(resolve, 100));
        onResetDataLayer();

        forceReloadAllData();
      } catch (error) {
        // Silent error handling
      }
    };

    setTimeout(handleResetTrigger, 50);
  }, [
    triggerReset,
    forceResetToInitial,
    onResetDataLayer,
    cancelAllRequests,
    resetAllFeatures,
    initialFeatures,
    forceReloadAllData,
    preserveVerticalLinesOnReset,
    clearAllLines,
    clearIntervalLines,
    loadingSensors,
    clearLoadingSensor,
  ]);
}
